import { Component, OnInit, ViewChild} from '@angular/core';
import {
	NxFileUploadConfig,
	NxFileUploader,
	NxFileUploaderComponent,
	FileItem
} from '@aposin/ng-aquila/file-uploader';
import {DocumentService} from "../../api/service/document.service";
import {DocumentMetadataService} from "../../api/service/documentMetadata.service";
import {AwsService} from "../../api/service/aws.service";
import {OAuthService} from 'angular-oauth2-oidc';
import {DocumentFileMetadata} from "../../api/model/documentFileMetadata";
import {DocumentMetadata} from "../../api/model/documentMetadata";
import {JadeUploadNotification} from "../../api/model/jadeUploadNotification";
import { NxMessageToastService } from '@aposin/ng-aquila/message';
import { SharedService } from '../service/shared.service';
import { ValidatedMetadataResponse } from '../../api/model/validatedMetadataResponse';
import { DocumentUploadResponse } from '../../api/model/documentUploadResponse';
import { HttpResponse} from '@angular/common/http';
import { ErrorDialogService } from '../interceptors/modal/error-dialog.service';

@Component({
	selector: 'app-upload-form',
	templateUrl: './upload-form.component.html',
	styleUrls: ['./upload-form.component.scss'],
})
export class UploadFormComponent implements OnInit {
	readonly toastUploadSuccessText = 'Das Dokument wurde erfolgreich importiert.';
	readonly acceptableFormatForDocument = 'text/plain, text/csv, image/tiff, image/bmp, image/jpeg, image/png, image/gif, ' +
    'application/msword, application/vnd.ms-powerpoint, application/vnd.ms-excel, .msg, .eml, application/vnd.ms-outlook, application/pdf, ' +
    'application/rtf, text/html, application/vnd.openxmlformats-officedocument.wordprocessingml.document, ' +
    'application/vnd.openxmlformats-officedocument.presentationml.presentation, ' +
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, message/rfc822';

	loader = true;
	isFormValid = true;
	importSimilar = false;
	documentMetadata: DocumentFileMetadata = {
    documentDate: this.formatDateGerman(new Date()), 
    confidentialityLevel: '2', 
    documentProcessed: '1',
    documentStatus: '0',    
  };
  readonly MAX_PDF_SIZE = 20 * 1024 * 1024;      // 20MB
  readonly MAX_OTHER_SIZE = 255 * 1024 * 1024;   // 256MB

  @ViewChild('documentUpload') documentUpload?: NxFileUploaderComponent;

	constructor(
    private readonly documentService: DocumentService,
    private readonly documentMetadataService: DocumentMetadataService,
    private readonly oauthService: OAuthService,
    private readonly messageToastService: NxMessageToastService,
    private readonly sharedService: SharedService,
    private readonly awsService: AwsService,
    private errorDialogService: ErrorDialogService,
	) {}

	ngOnInit(): void {
	  this.documentService.configuration.apiKeys = {Authorization: `Bearer ${this.oauthService.getAccessToken()}` };
	  let dui: string = this.documentMetadata.dui || "";
	  if (!!dui) {
	    this.documentMetadataService.documentMetadataGet(dui).subscribe((documentMetadata: DocumentMetadata) => {
	      this.documentMetadata = documentMetadata;
	      if (this.importSimilar) {
	        if (this.documentMetadata.jadedui) delete this.documentMetadata.jadedui;
          if (this.documentMetadata.jadeDocumentInfo) delete this.documentMetadata.jadeDocumentInfo;
          if (this.documentMetadata.jadeDocumentStatus) delete this.documentMetadata.jadeDocumentStatus;
          if (this.documentMetadata.jadeDocumentError) delete this.documentMetadata.jadeDocumentError;
          if (this.documentMetadata.version) delete this.documentMetadata.version;
          if (this.documentMetadata.lastVersion) delete this.documentMetadata.lastVersion;
          if (this.documentMetadata.createdAtDateTime) delete this.documentMetadata.createdAtDateTime;
          if (this.documentMetadata.createdBy) delete this.documentMetadata.createdBy;
          if (this.documentMetadata.modifiedAtDateTime) delete this.documentMetadata.modifiedAtDateTime;
          if (this.documentMetadata.modifiedBy) delete this.documentMetadata.modifiedBy;
          if (this.documentMetadata.s3Path) delete this.documentMetadata.s3Path;
          if (this.documentMetadata.emailSubject) delete this.documentMetadata.emailSubject;
          if (this.documentMetadata.emailSendTimestamp) delete this.documentMetadata.emailSendTimestamp;
          if (this.documentMetadata.loadDate) delete this.documentMetadata.loadDate;
          if (this.documentMetadata.loadTime) delete this.documentMetadata.loadTime;
          if (this.documentMetadata.loadUser) delete this.documentMetadata.loadUser;
          if (this.documentMetadata.changeUser) delete this.documentMetadata.changeUser;
	      }
	      this.loader = false;
			}, error => {
		      this.loader = false;
			    throw error;
			  }
			);
	  }
	  this.loader = false;
	}

  onFileChange(files: FileItem[]): void {
    if (!files || !files.length) {
      return;
    }

    const file = files[0].file;
    if (file == null || file == undefined){
      return
    }
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    const maxSize = isPdf ? this.MAX_PDF_SIZE : this.MAX_OTHER_SIZE;

    if (file.size > maxSize) {
      // clearing only the model leaves the file in the uploader (ngModel sees no change)
      this.documentUpload?.removeFile(files[0]);
      this.errorDialogService.openDialog({
        title: 'Datei zu groß',
        message: `Das Dokument übersteigt das gültige Dateigrößenlimit. Bitte die Datei möglichst aufteilen und in kleineren Portionen hochladen.`,
        errorCode: 'FILE_SIZE_EXCEEDED'
      });
    }
  }

  get hasFile(): boolean {
    // the uploader writes FileItem[] into the model, despite its Blob type
    const items = this.documentMetadata.file as unknown as FileItem[] | undefined;
    return !!items?.length;
  }

  upload() {
    let formData: any = this.buildFormData();
    if (!formData.get('fileName') && !formData.get('jadedui')) {
      this.errorDialogService.openDialog({
        title: 'Keine Datei ausgewählt',
        message: 'Bitte eine Datei auswählen.',
        errorCode: 'FILE_MISSING'
      });
      return;
    }
    let file: any = formData.get('file');
    if (file?.type){
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const maxSize = isPdf ? this.MAX_PDF_SIZE : this.MAX_OTHER_SIZE;

      if (file.size > maxSize) {
        this.errorDialogService.openDialog({
          title: 'Datei zu groß',
          message: `Das Dokument übersteigt das gültige Dateigrößenlimit. Bitte die Datei möglichst aufteilen und in kleineren Portionen hochladen.`,
          errorCode: 'FILE_SIZE_EXCEEDED'
        });
        return;
      }
    }
    formData.delete('file');
    this.loader = true;
    const formDataJson = this.getFormDataAsJson(formData);

    this.documentService.documentPost(formDataJson).subscribe(
      (response: DocumentUploadResponse) => {
        if (!!formData.get('jadedui')) {
          this.sharedService.sendMessage('detailsLink', response.dui);
          this.toastUploadSuccess();
          this.loader = false;
        } else {
          this.awsService.documentS3Upload(response.presignedUrl, file, 'response').subscribe(
            (uploadResponse: HttpResponse<any>) => {
              this.sharedService.sendMessage('detailsLink', response.dui);
              this.toastUploadSuccess();
              this.loader = false;
              const headersObj: { [key: string]: string | null } = {};
                uploadResponse.headers.keys().forEach((key: string) => {
                  headersObj[key] = uploadResponse.headers.get(key);
              });
              const responseWithHeaders = {
                body: uploadResponse.body,
                status: uploadResponse.status,
                statusText: uploadResponse.statusText,
                headers: headersObj
              };
              const responseJson = JSON.stringify(responseWithHeaders, null, 2);
              this.sendJadeUploadNotification(response.dui, 'UPLOADED_TO_JADE', responseJson, formData.get('documentStatus'), formData.get('documentNotes'));
            },
            (error: any) => {
              // this.loader = false;

              const errorHeadersObj: { [key: string]: string | null } = {};

              if (error && error.headers) {
                error.headers.keys().forEach((key: string) => {
                  errorHeadersObj[key] = error.headers.get(key);
                });
              }

              const errorWithHeaders = {
                message: error.message || 'Unknown error',
                status: error.status || 'Unknown status',
                statusText: error.statusText || 'Unknown status text',
                headers: errorHeadersObj,
                details: `${JSON.stringify(error, null, 2)}`
              };
              const firstResponseDui = response.dui
              const errorJson = JSON.stringify(errorWithHeaders, null, 2);
              this.sendJadeUploadNotification(firstResponseDui, 'FAILED_UPLOADING_TO_JADE', errorJson, "90", "UPLOAD_FAILED");
              ////////////////////////////////////retry
              //////////////////////////////////////////
              setTimeout(() => {   
                let formDataJsonSecond = formDataJson
                if (!!formData.get('documentNotes') && formData.get('documentNotes') === 'testaztenc4991failingfirst' &&
                    !!formData.get('documentTitle') && formData.get('documentTitle') === 'testaztenc4991' ){
                      formData.documentNotes = 'testaztenc4991success'
                      formDataJsonSecond = this.getFormDataAsJson(formData);
                }
                this.documentService.documentPost(formDataJsonSecond).subscribe(
                  (response: DocumentUploadResponse) => {                  
                    if (!!formData.get('jadedui')) {
                      this.sharedService.sendMessage('detailsLink', response.dui);
                      this.toastUploadSuccess();
                      this.loader = false;
                    } else {
                      this.awsService.documentS3Upload(response.presignedUrl, file, 'response').subscribe(
                        (uploadResponse: HttpResponse<any>) => {
                          this.sharedService.sendMessage('detailsLink', response.dui);
                          this.toastUploadSuccess();
                          this.loader = false;
                          const headersObj: { [key: string]: string | null } = {};
                            uploadResponse.headers.keys().forEach((key: string) => {
                              headersObj[key] = uploadResponse.headers.get(key);
                          });
                          const responseWithHeaders = {
                            body: uploadResponse.body,
                            status: uploadResponse.status,
                            statusText: uploadResponse.statusText,
                            headers: headersObj
                          };
                          const responseJson = JSON.stringify(responseWithHeaders, null, 2);
                          this.sendJadeUploadNotification(response.dui, 'UPLOADED_TO_JADE_RETRY', responseJson, formData.get('documentStatus'), formData.get('documentNotes'));
                        },
                        (error: any) => {
                          this.loader = false;
            
                          const errorHeadersObj: { [key: string]: string | null } = {};
            
                          if (error && error.headers) {
                            error.headers.keys().forEach((key: string) => {
                              errorHeadersObj[key] = error.headers.get(key);
                            });
                          }
            
                          const errorWithHeaders = {
                            message: error.message || 'Unknown error',
                            status: error.status || 'Unknown status',
                            statusText: error.statusText || 'Unknown status text',
                            headers: errorHeadersObj,
                            details: `${JSON.stringify(error, null, 2)}`
                          };
            
                          const errorJson = JSON.stringify(errorWithHeaders, null, 2);
                          this.sendJadeUploadNotification(response.dui, 'FAILED_UPLOADING_TO_JADE_RETRY', errorJson, "90", "UPLOAD_FAILED");

                          let message = 'Unknown error';
                          if (error instanceof Error) {
                            message = `${error.name}: ${error.message}\nStack:\n${error.stack}`;
                          } else if (typeof error === 'string') {
                            message = error;
                          } else {
                            try {
                              message = JSON.stringify(error, null, 2);
                            } catch {
                              message = String(error);
                            }
                          }
                          console.log(`UploadError: ${message}`);
                          let details: string = `${message} ${errorWithHeaders.message}`
                          const msg: string = `Leider ist beim Upload-Prozess ein Fehler aufgetreten (bedingt durch eine Verbindungsunterbrechung wie z.B. temporäre Server- oder Netzwerkprobleme.) Trotz mehrerer, automatisch angestoßener Wiederholungsversuche konnte das Dokument nicht erfolgreich hochgeladen werden. Die fehlgeschlagenen Upload-Versuche wurden automatisch als "logisch gelöscht" gekennzeichnet. Bitte versuche, den Upload zu einem späteren Zeitpunkt erneut durchzuführen.`;
                          var businessError = { message: msg, errorCode: details, title: "Fehlermeldung: Datei-Upload fehlgeschlagen:"};
                          this.errorDialogService.openDialog(businessError);	
                          // throw error;
                        }
                      );
                    }
                  },
                  error => {
                    this.loader = false;
                    throw error;
                  }
                );
              }, 40000);
              /////////////////////////////////////////

              // throw error;
            }
          );
        }
      },
      error => {
        this.loader = false;
        throw error;
      }
    );
  }

	sendJadeUploadNotification(dui: string, jadeDocumentStatus: string, jadeDocumentInfo: string, documentStatus: string, documentNotes: string) {
   let documentStatusValue = documentStatus
   if (!documentStatusValue){
    documentStatusValue = '0'
   }
   const jadeUploadNotification: JadeUploadNotification = {
      dui: dui,
      jadeDocumentStatus: jadeDocumentStatus,
      jadeDocumentInfo: jadeDocumentInfo,
      documentStatus: documentStatusValue,
      documentNotes: documentNotes,
    };
	  this.documentService.jadeUploadNotification(jadeUploadNotification).subscribe( _ => {
	    console.log('Send Jade upload notification!');
	  }, error => {
	    console.log('Failed to send Jade upload notification!');
	  });
	}

	getFormDataAsJson(formData: FormData) {
		const jsonObject: { [key: string]: any } = {};
		formData.forEach((value: any, key: string) => {
			jsonObject[key] = value;
		});
		return JSON.stringify(jsonObject);
	}

	buildFormData(): FormData {
		let formData: any = new FormData();    
		Object.entries(this.documentMetadata).forEach(([key, value]) => {
			if (!!value) {
				if (key === 'file'){
					// a removed file leaves [] in the model
					if (value.length) {
						formData.append("documentMimeType", value[0].file.type);
						formData.append("fileName", value[0].file.name);
						formData.append(key, value[0].file);
					}
				} else {
					formData.append(key, value);
				}
			}
		});
		return formData;
	}

	onAllValidChanged(allValid: boolean) {
	  this.isFormValid = allValid;
	}

	toastUploadSuccess() {
		this.messageToastService.open(this.toastUploadSuccessText, {
			context: 'success',
			duration: 5000,
		});
	}

  formatDateGerman(date: Date | string | null): string {
    if (!date) return '';
    const d = new Date(date);//2025-10-29
    return `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
  }
}